import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Plane, Star, Send, CheckCircle, AlertCircle, Phone, 
  MapPin, Award, Heart, Shield, Clock, Globe, Compass, Target,
  ThumbsUp, Briefcase, Calendar, Mail, MessageCircle,
  Building2, Headphones, DollarSign, Sparkles, CheckCircle2, Users2,
  Palmtree, Mountain, Ship, Hotel, Car, CreditCard, Languages, BadgeCheck, Smartphone, Handshake
} from "lucide-react";

const APropos = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setSubmitStatus("error");
      setStatusMessage("Veuillez remplir tous les champs obligatoires.");
      setTimeout(() => setSubmitStatus(null), 5000);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Détecter l'environnement
      const apiUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        ? '/backend_djatervoyage/email/send_contact.php'  // En local via proxy
        : `${process.env.REACT_APP_BACKEND_URL}/email/send_contact.php`; // En production

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData)
      });

      // Vérifier si la réponse est ok
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      // Vérifier le type de contenu
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("Réponse non-JSON reçue:", text.substring(0, 200));
        throw new Error("Le serveur n'a pas renvoyé de JSON valide");
      }

      const data = await response.json();

      if (data.success) {
        setSubmitStatus("success");
        setStatusMessage(data.message || "Message envoyé avec succès ! Nous vous répondrons bientôt.");
        setFormData({
          name: "",
          email: "",
          phone: "",
          message: ""
        });
      } else {
        setSubmitStatus("error");
        setStatusMessage(data.error || "Une erreur est survenue lors de l'envoi du message.");
      }
    } catch (error) {
      setSubmitStatus("error");
      setStatusMessage(`Erreur: ${error.message || "Problème de connexion"}`);
      console.error("Erreur complète:", error);
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 8000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
      
      {/* Hero Banner avec vidéo/image background style */}
      <div className="relative bg-gradient-to-r from-blue-900 via-blue-700 to-purple-900 text-white overflow-hidden">
        
        
        <div className="absolute inset-0 bg-black/30"></div>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.1 }}
          className="absolute inset-0"
          style={{
            backgroundImage: "url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')"
          }}
        />
        
        <div className="container mx-auto px-6 py-20 relative z-10">
                 
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-5xl mx-auto"
          >
             <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source
            src="./videos/video1.mp4"
            type="video/mp4"
          />
        </video>
            <motion.div 
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-block mb-6"
            >

              
              <div className="relative">
               
                <img src="logo.png" alt="Logo Djater Voyage" className="w-80 h-80 mx-auto" />
             
              </div>
            </motion.div>
            

            
            <p className="text-2xl  font-light mb-8 text-blue-100">
         
            </p>
            
            <p className="text-2xl md:text-3xl max-w-3xl mx-auto leading-relaxed opacity-90">
                   Votre Partenaire de Confiance pour Explorer le Monde
                </p>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-10 flex flex-wrap justify-center gap-4"
            >
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <span className="font-semibold">Certifié & Agréé IATA</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <BadgeCheck className="w-5 h-5 text-blue-400" />
                <span className="font-semibold">1,000+ Clients</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full">
                <Award className="w-5 h-5 text-yellow-400" />
                <span className="font-semibold">Prix Excellence</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-50 to-transparent"></div>
      </div>

      <div className="container mx-auto px-6 py-16">
        
        {/* Notre Histoire - Version Enrichie */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-24"
        >
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Compass className="w-8 h-8 text-blue-600" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900">Notre Histoire</h2>
              </div>
              
              <div className="space-y-5 text-gray-700 text-lg leading-relaxed">
                <p className="text-xl font-semibold text-blue-700">
                  Une passion transformée en expertise
                </p>
                <p>
                  <strong className="text-blue-600">Djater Voyage</strong> est née 
                  d'une vision audacieuse : démocratiser le voyage de qualité et le rendre accessible 
                  à tous. Ce qui a commencé dans un petit bureau avec une équipe de 3 passionnés est 
                  devenu aujourd'hui l'une des agences de voyage en ligne les plus prisées d'Afrique de l'Ouest.
                </p>
                <p>
                  Notre croissance spectaculaire témoigne de notre engagement indéfectible envers 
                  l'excellence. Avec <span className="font-bold text-blue-600">plus de 1 000 voyageurs satisfaits</span>, 
                  nous avons organisé des voyages vers <span className="font-bold text-blue-600">120+ destinations</span> 
                  sur tous les continents.
                </p>
                <p>
                  Chaque membre de notre équipe de <span className="font-bold text-blue-600">25+ experts</span> partage 
                  la même passion : créer des expériences de voyage exceptionnelles qui dépassent vos attentes 
                  et créent des souvenirs impérissables.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl p-8 shadow-2xl">
                <div className="grid grid-cols-2 gap-6 text-white">
                  {[
                    { icon: <Calendar className="w-10 h-10" />, value: "5+", label: "Années d'Expertise" },
                    { icon: <Users2 className="w-10 h-10" />, value: "1K+", label: "Clients Heureux" },
                    { icon: <MapPin className="w-10 h-10" />, value: "120+", label: "Destinations" },
                    { icon: <Award className="w-10 h-10" />, value: "98%", label: "Satisfaction Client" }
                  ].map((stat, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, scale: 0.5 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                      className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center hover:bg-white/20 transition"
                    >
                      <div className="flex justify-center mb-3">{stat.icon}</div>
                      <div className="text-4xl font-black mb-1">{stat.value}</div>
                      <div className="text-sm opacity-90">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-yellow-400 rounded-full blur-3xl opacity-20"></div>
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-blue-400 rounded-full blur-3xl opacity-20"></div>
            </div>
          </div>
        </motion.section>

        {/* Mission, Vision, Valeurs */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <Target className="w-12 h-12 text-blue-600 inline-block mr-3" />
              Notre Philosophie
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Les valeurs qui guident chacune de nos actions
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Target className="w-12 h-12 text-blue-600" />,
                title: "Notre Mission",
                subtitle: "Rendre le voyage accessible",
                desc: "Démocratiser l'accès au voyage de qualité en proposant des services exceptionnels à des tarifs compétitifs, sans jamais compromettre sur l'excellence.",
                color: "blue"
              },
              {
                icon: <Sparkles className="w-12 h-12 text-purple-600" />,
                title: "Notre Vision",
                subtitle: "Leader du voyage en Afrique",
                desc: "Devenir la référence incontournable du voyage en Afrique de l'Ouest en innovant constamment et en plaçant nos clients au cœur de tout ce que nous faisons.",
                color: "purple"
              },
              {
                icon: <Heart className="w-12 h-12 text-red-600" />,
                title: "Nos Valeurs",
                subtitle: "Excellence & Intégrité",
                desc: "Engagement envers l'excellence, intégrité dans chaque transaction, passion pour le service client et innovation continue pour votre satisfaction.",
                color: "red"
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="bg-white rounded-3xl shadow-xl p-8 hover:shadow-2xl transition-all border-t-4 border-blue-600"
              >
                <div className="mb-6">{item.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm font-semibold text-blue-600 mb-4">{item.subtitle}</p>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Services Complets */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <Briefcase className="w-12 h-12 text-blue-600 inline-block mr-3" />
              Nos Services Premium
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une gamme complète de services pour répondre à tous vos besoins de voyage
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { icon: <Plane className="w-10 h-10" />, title: "Vols Internationaux", desc: "Billets d'avion vers 120+ destinations", color: "bg-blue-500" },
              { icon: <Hotel className="w-10 h-10" />, title: "Hébergements", desc: "Hôtels 3 à 5 étoiles sélectionnés", color: "bg-purple-500" },
              { icon: <Car className="w-10 h-10" />, title: "Location de Véhicules", desc: "Voitures, bus et transferts VIP", color: "bg-green-500" },
              { icon: <Ship className="w-10 h-10" />, title: "Croisières", desc: "Voyages maritimes d'exception", color: "bg-cyan-500" },
              { icon: <Mountain className="w-10 h-10" />, title: "Circuits Aventure", desc: "Expéditions guidées et treks", color: "bg-orange-500" },
              { icon: <Palmtree className="w-10 h-10" />, title: "Séjours Balnéaires", desc: "Plages paradisiaques et resorts", color: "bg-teal-500" },
              { icon: <Building2 className="w-10 h-10" />, title: "Voyages d'Affaires", desc: "Solutions corporate sur mesure", color: "bg-gray-700" },
              { icon: <Languages className="w-10 h-10" />, title: "Visas & Documents", desc: "Assistance administrative complète", color: "bg-indigo-500" }
            ].map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ scale: 1.05 }}
                className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-2xl transition-all group cursor-pointer"
              >
                <div className={`${service.color} w-16 h-16 rounded-xl flex items-center justify-center mb-4 text-white group-hover:scale-110 transition-transform`}>
                  {service.icon}
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600 text-sm">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Moyens de Paiement */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <CreditCard className="w-12 h-12 text-blue-600 inline-block mr-3" />
              Moyens de Paiement Acceptés
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nous proposons des solutions de paiement sécurisées et variées pour faciliter vos transactions
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { 
                icon: <CreditCard className="w-12 h-12" />, 
                title: "Cartes Bancaires", 
                desc: "Visa, Mastercard, American Express",
                color: "from-blue-500 to-blue-600",
                features: ["Paiement sécurisé 3D Secure", "Sans frais supplémentaires"]
              },
              { 
                icon: <Smartphone className="w-12 h-12" />, 
                title: "Mobile Money", 
                desc: "Orange Money, MTN Mobile Money, Wave",
                color: "from-green-500 to-green-600",
                features: ["Paiement instantané", "Disponible 24h/24"]
              },
              { 
                icon: <DollarSign className="w-12 h-12" />, 
                title: "Transfert Bancaire", 
                desc: "Virement SEPA, Western Union, MoneyGram",
                color: "from-purple-500 to-purple-600",
                features: ["Tracabilité garantie", "Sécurité maximale"]
              },
              { 
                icon: <Globe className="w-12 h-12" />, 
                title: "PayPal", 
                desc: "Paiement en ligne sécurisé",
                color: "from-indigo-500 to-indigo-600",
                features: ["Protection acheteur", "Paiement en devises"]
              }
            ].map((method, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all"
              >
                <div className={`bg-gradient-to-r ${method.color} p-5 flex justify-center text-white`}>
                  {method.icon}
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{method.title}</h3>
                  <p className="text-gray-600 text-sm mb-3">{method.desc}</p>
                  <ul className="space-y-1">
                    {method.features.map((feat, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-green-600">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {feat}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-8 bg-blue-50 rounded-2xl p-6 max-w-3xl mx-auto border border-blue-200">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Paiement 100% sécurisé</p>
                <p className="text-sm text-gray-600 mt-1">
                  Toutes vos transactions sont cryptées et sécurisées. Nous utilisons les technologies SSL les plus récentes 
                  pour protéger vos données bancaires. Aucun frais caché, le prix affiché est le prix final.
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Nos Partenaires */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <Handshake className="w-12 h-12 text-blue-600 inline-block mr-3" />
              Nos Partenaires de Confiance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nous collaborons avec des partenaires de renom pour vous offrir le meilleur service
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Nissi Assurance */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all col-span-1 md:col-span-3 max-w-md mx-auto w-full"
            >
              <div className="flex flex-col items-center text-center">
                <img 
                  src="./logo_p.jpg" 
                  alt="Logo Nissi Assurance" 
                  className="w-32 h-32 object-contain mb-4 rounded-xl"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden w-24 h-24 bg-blue-100 rounded-full items-center justify-center mb-4">
                  <Shield className="w-12 h-12 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Nissi Assurance</h3>
                <p className="text-blue-600 font-semibold mb-4">Assurance Voyage</p>
                <p className="text-gray-600 leading-relaxed">
                  Partenaire officiel pour l'assurance voyage. Nissi Assurance vous couvre pendant toute la durée 
                  de votre séjour : annulation, bagages, rapatriement, frais médicaux. Voyagez l'esprit tranquille 
                  grâce à notre partenariat privilégié.
                </p>
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  {["Annulation", "Bagages", "Rapatriement", "Médical", "Assistance 24/7"].map((item, i) => (
                    <span key={i} className="bg-blue-50 text-blue-700 text-xs font-medium px-3 py-1 rounded-full">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Autres partenaires à venir */}
          <div className="mt-8 text-center">
            <p className="text-gray-500 text-sm">
              De nouvelles collaborations à venir — restez connectés !
            </p>
          </div>
        </motion.section>

        {/* Avantages Compétitifs */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24 bg-gradient-to-br from-blue-600 to-purple-700 rounded-3xl p-12 text-white shadow-2xl"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
            <Star className="w-12 h-12 inline-block mr-3" />
            Pourquoi Choisir Djater Voyage ?
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: <Shield className="w-10 h-10" />, title: "Sécurité Maximale", desc: "Transactions 100% sécurisées et données cryptées" },
              { icon: <DollarSign className="w-10 h-10" />, title: "Meilleurs Prix Garantis", desc: "Politique de meilleur prix ou remboursement de la différence" },
              { icon: <Headphones className="w-10 h-10" />, title: "Support 24/7", desc: "Assistance client disponible à toute heure, 7j/7" },
              { icon: <CheckCircle2 className="w-10 h-10" />, title: "Sans Frais Cachés", desc: "Transparence totale sur tous les prix affichés" },
              { icon: <CreditCard className="w-10 h-10" />, title: "Paiement Flexible", desc: "Plusieurs options de paiement sécurisées" },
              { icon: <ThumbsUp className="w-10 h-10" />, title: "Satisfaction Garantie", desc: "98% de nos clients recommandent nos services" }
            ].map((advantage, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition"
              >
                <div className="flex-shrink-0">{advantage.icon}</div>
                <div>
                  <h3 className="font-bold text-lg mb-2">{advantage.title}</h3>
                  <p className="text-white/80 text-sm">{advantage.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Témoignages Clients */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <MessageCircle className="w-12 h-12 text-blue-600 inline-block mr-3" />
              Ce Que Disent Nos Clients
            </h2>
            <p className="text-xl text-gray-600">Des milliers de voyageurs satisfaits partagent leur expérience</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Marie K.",
                role: "Voyageuse Solo",
                avatar: "👩🏽",
                rating: 5,
                comment: "Service exceptionnel ! L'équipe de Djater Voyage a organisé mon tour d'Europe de façon impeccable. Je recommande à 100% !",
                destination: "Europe - 15 jours"
              },
              {
                name: "Jean-Baptiste D.",
                role: "Voyage d'Affaires",
                avatar: "👨🏾‍💼",
                rating: 5,
                comment: "Professionnalisme remarquable. Mes voyages d'affaires sont toujours parfaitement organisés, je gagne un temps précieux.",
                destination: "Paris - Londres"
              },
              {
                name: "Aminata S.",
                role: "Voyage en Famille",
                avatar: "👩🏿",
                rating: 5,
                comment: "Notre lune de miel aux Maldives était un rêve devenu réalité ! Attention aux détails et service personnalisé exceptionnel.",
                destination: "Maldives - 10 jours"
              }
            ].map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                whileHover={{ y: -5 }}
                className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-5xl">{testimonial.avatar}</div>
                  <div>
                    <h4 className="font-bold text-lg text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.comment}"</p>
                <div className="flex items-center gap-2 text-sm text-blue-600 font-semibold">
                  <MapPin className="w-4 h-4" />
                  {testimonial.destination}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Section Contact Enrichie */}
        <motion.section
          id="contact"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-24 bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-3xl shadow-2xl p-12"
        >
          <div className="text-center mb-12">
            <MessageCircle className="w-16 h-16 text-blue-600 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Parlons de Votre Prochain Voyage
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Notre équipe d'experts est prête à transformer vos rêves en réalité. 
              Contactez-nous dès maintenant pour une consultation personnalisée gratuite.
            </p>
          </div>
        
        <AnimatePresence>
          {submitStatus && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`max-w-2xl mx-auto mb-6 p-4 rounded-lg flex items-center gap-3 ${
                submitStatus === "success" 
                  ? "bg-green-100 border border-green-400 text-green-800" 
                  : "bg-red-100 border border-red-400 text-red-800"
              }`}
            >
              {submitStatus === "success" ? (
                <CheckCircle className="w-6 h-6 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-6 h-6 flex-shrink-0" />
              )}
              <p className="flex-1">{statusMessage}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-4">
          <div>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Votre nom *"
              required
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none transition"
              disabled={isSubmitting}
            />
          </div>
          
          <div>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Votre email *"
              required
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none transition"
              disabled={isSubmitting}
            />
          </div>

          <div>
            <div className="relative">
              <Phone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Votre téléphone (optionnel)"
                className="w-full p-3 pl-10 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none transition"
                disabled={isSubmitting}
              />
            </div>
          </div>
          
          <div>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Votre message *"
              rows="5"
              required
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none transition resize-none"
              disabled={isSubmitting}
            ></textarea>
          </div>
          
          <div className="flex justify-center">
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
              className={`flex items-center gap-2 font-bold py-3 px-8 rounded-full shadow-xl transition ${
                isSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-700 hover:bg-blue-800 text-white"
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Envoyer le message
                </>
              )}
            </motion.button>
          </div>
          
          <p className="text-center text-sm text-gray-500 mt-4">
            * Champs obligatoires
          </p>
        </form>

        {/* Coordonnées de Contact */}
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              icon: <Mail className="w-10 h-10 text-blue-600" />,
              title: "Email",
              value: "djatervoyage@gmail.com",
              link: "mailto:djatervoyage@gmail.com",
              bg: "bg-blue-50"
            },
            {
              icon: <Phone className="w-10 h-10 text-green-600" />,
              title: "Téléphone",
              value: "(+225) 27 21 54 46 59 / 07 07 07 86 74",
              link: "tel:+225271544659",
              bg: "bg-green-50"
            },
            {
              icon: <Clock className="w-10 h-10 text-purple-600" />,
              title: "Horaires",
              value: "Lun-Sam: 8h-18h",
              subtitle: "Support 24/7",
              bg: "bg-purple-50"
            },
            {
              icon: <MapPin className="w-10 h-10 text-red-600" />,
              title: "Localisation",
              value: "Abidjan, Côte d'Ivoire",
              subtitle: "Immeuble Baobab au 3e - Notre Dame Treichville (Abidjan)",
              bg: "bg-red-50"
            }
          ].map((contact, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ y: -5 }}
              className={`${contact.bg} rounded-2xl p-6 text-center shadow-md hover:shadow-xl transition-all`}
            >
              <div className="flex justify-center mb-4">{contact.icon}</div>
              <h4 className="font-bold text-gray-900 mb-2">{contact.title}</h4>
              {contact.link ? (
                <a href={contact.link} className="text-blue-600 hover:underline font-semibold block">
                  {contact.value}
                </a>
              ) : (
                <p className="text-gray-700 font-semibold">{contact.value}</p>
              )}
              {contact.subtitle && (
                <p className="text-sm text-gray-600 mt-1">{contact.subtitle}</p>
              )}
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Appel à l'Action Final */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mb-16 relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-3xl shadow-2xl p-16 text-white text-center"
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: "url('data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')"
          }}></div>
        </div>
        
        <div className="relative z-10">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="inline-block mb-6"
          >
            <Globe className="w-20 h-20 mx-auto" />
          </motion.div>
          
          <h2 className="text-5xl md:text-6xl font-black mb-6">
            Prêt pour l'Aventure de Votre Vie ?
          </h2>
          
          <p className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto opacity-95">
            Rejoignez plus de 1 000 voyageurs qui ont fait confiance à Djater Voyage. 
            Laissez-nous créer des souvenirs inoubliables pour vous !
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <motion.a
              href="/"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 bg-white text-blue-600 font-bold py-4 px-10 rounded-full shadow-2xl hover:shadow-3xl transition-all"
            >
              <Plane className="w-6 h-6" />
              Explorer Nos Offres
            </motion.a>
            
            <motion.a
              href="#contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border-2 border-white text-white font-bold py-4 px-10 rounded-full hover:bg-white/20 transition-all"
            >
              <MessageCircle className="w-6 h-6" />
              Demander un Devis
            </motion.a>
          </div>
          
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              <span>Paiement Sécurisé</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              <span>Meilleur Prix Garanti</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              <span>Support 24/7</span>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Footer Info */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="text-center py-8 text-gray-600"
      >
        <p className="text-sm">
           Djater Voyage. Tous droits réservés. | 
          <span className="text-blue-600 font-semibold"> Agence agréée et certifiée</span>
        </p>
      </motion.div>

      </div>
    </div>
  );
};

export default APropos;
